import { NavigationList } from '@app/utils/lists/navigation-list/navigation-list';
import { loggerFor } from '@app/utils/logger';
import { WithKey } from '@app/utils/object-extensions/with-key';
import { BehaviorSubject, Observable, Subject } from 'rxjs';

export class NavigationListImpl<T extends WithKey> implements NavigationList<T> {
  private _active$: Subject<T> = new Subject<T>();

  private _activeIndex: number = -1;
  private _activeIndex$: Subject<number> = new BehaviorSubject<number>(this._activeIndex);

  private _entries: T[] = [];
  private _entries$: Subject<T[]> = new BehaviorSubject<T[]>([]);

  private _highlighted$: Subject<T> = new Subject<T>();

  private _highlightedIndex: number = -1;
  private _highlightedIndex$: Subject<number> = new BehaviorSubject<number>(this._highlightedIndex);

  private _sortBy?: (a: T, b: T) => number;

  private logger = loggerFor('NavigationList');

  get active(): T {
    return this._entries[this._activeIndex];
  }

  get active$(): Observable<T> {
    return this._active$;
  }

  get activeIndex(): number {
    return this._activeIndex;
  }

  get activeIndex$(): Observable<number> {
    return this._activeIndex$;
  }

  get entries(): T[] {
    return this._entries;
  }

  get entries$(): Observable<T[]> {
    return this._entries$;
  }

  get highlighted(): T {
    return this._entries[this._highlightedIndex];
  }

  get highlighted$(): Observable<T> {
    return this._highlighted$;
  }

  get highlightedIndex(): number {
    return this._highlightedIndex;
  }

  get highlightedIndex$(): Observable<number> {
    return this._highlightedIndex$;
  }

  constructor(_entries: T[] = []) {
    this._entries = _entries;

    this.draw();
  }

  public activate(entryOrIndex: number | T): this {
    if (typeof entryOrIndex === 'number') {
      this.setActiveEntry(this.entries[entryOrIndex]);
    } else {
      this.setActiveEntry(entryOrIndex);
    }

    return this;
  }

  public clearActive(): this {
    this.setActiveEntry(undefined);

    return this;
  }

  public clearHighlighted(): this {
    this.setHighlightedEntry(undefined);

    return this;
  }

  public draw(): this {
    // this.log();

    this.emitHighlightedIndex();
    this.emitHighlighted();

    this.emitActiveIndex();
    this.emitActive();

    this.emitEntries();

    return this;
  }

  public highlight(entryOrIndex: number | T): this {
    if (typeof entryOrIndex === 'number') {
      this.setHighlightedEntry(this.entries[entryOrIndex]);
    } else {
      this.setHighlightedEntry(entryOrIndex);
    }

    return this;
  }

  public markActive(): this {
    this._activeIndex = this._highlightedIndex;

    return this;
  }

  public next(): this {
    this.step(1);

    return this;
  }

  public previous(): this {
    this.step(-1);

    return this;
  }

  public reset(): this {
    this._highlightedIndex = 0;
    this._activeIndex = -1;

    return this;
  }

  public setEntries(entries: T[]): this {
    this._entries = entries.slice();

    if (this._sortBy != null) {
      this._entries.sort((a, b) => this._sortBy(a, b));
    }

    return this;
  }

  public sortBy(compareFunction?: (a: T, b: T) => number): this {
    this._sortBy = compareFunction;

    if (this._entries != null && this._sortBy != null) {
      this._entries.sort((a, b) => this._sortBy(a, b));
    }

    return this;
  }

  public step(direction: number): this {
    if (direction > 0) {
      // Make sure that the currently selected entry isn't the last one
      if (this._entries.length - 1 > this._highlightedIndex) {
        const nextItem = this._entries[this._highlightedIndex + 1];

        if (nextItem.key === this.active?.key) {
          // Handle the edge case that the selected key is the last entry in the list
          if (this._entries.length - 2 > this._highlightedIndex) {
            // Jump over the selected entry (i.e. select the entry after that one)
            this._highlightedIndex += 2;
          }
        } else {
          // Highlight the next entry
          ++this._highlightedIndex;
        }
      }
    } else if (direction < 0) {
      // Make sure that the currently selected entry isn't the first one
      if (this._highlightedIndex > 0) {
        const nextItem = this._entries[this._highlightedIndex - 1];

        if (nextItem.key === this.active?.key) {
          // Handle the edge case that the selected key is the first entry in the list
          if (this._highlightedIndex > 1) {
            // Jump over the selected entry (i.e. select the entry before that one)
            this._highlightedIndex -= 2;
          }
        } else {
          // Highlight the previous entry
          --this._highlightedIndex;
        }
      }
    }

    this.log();

    this.emitHighlightedIndex();
    this.emitHighlighted();

    return this;
  }

  private emitActive(): void {
    if (this.active !== undefined) {
      this._active$.next(this.active);
    }
  }

  private emitActiveIndex(): void {
    this._activeIndex$.next(this._activeIndex);
  }

  private emitEntries(): void {
    this._entries$.next(this._entries);
  }

  private emitHighlighted(): void {
    this._highlighted$.next(this.highlighted);
  }

  private emitHighlightedIndex(): void {
    this._highlightedIndex$.next(this._highlightedIndex);
  }

  private findIndex(entry: T) {
    return this._entries.findIndex((_entry) => _entry.key === entry.key);
  }

  private log(): void {
    this.logger.debug(`Active index:      ${this.activeIndex}`);
    this.logger.debug(`Highlighted index: ${this.highlightedIndex}`);
  }

  private setActiveEntry(entry: T | undefined): this {
    if (entry !== undefined) {
      this._activeIndex = this.findIndex(entry);
    } else {
      this._activeIndex = -1;
    }

    return this;
  }

  private setHighlightedEntry(entry: T | undefined): this {
    if (entry !== undefined) {
      this._highlightedIndex = this.findIndex(entry);
    } else {
      this._highlightedIndex = -1;
    }

    return this;
  }
}
